import React, { useMemo, useState, useRef, useEffect } from "react";
import { TrendingUp, TrendingDown, Scale, Clock, AlertTriangle, FileDown, Target, X, Plus } from "lucide-react";
import { formatBRL, formatDate } from "../context/AppContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, BarChart,
} from "recharts";

const PALETTE = ["#2563eb", "#059669", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#db2777", "#65a30d", "#ea580c", "#4f46e5", "#0d9488", "#b45309"];
const NOMES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const monthLabel = (ym) => { const [y, m] = ym.split("-"); return `${NOMES[Number(m) - 1]}/${y.slice(2)}`; };
const diasNoMes = (y, m) => new Date(y, m, 0).getDate();
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hojeISO = () => new Date().toISOString().slice(0, 10);

const Kpi = ({ icon: Icon, label, value, tint, testid, hint }) => (
  <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-400 text-xs font-medium">
      <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-4 h-4" /></span>
      {label}
    </div>
    <p className="text-xl font-bold text-slate-800 mt-2">{value}</p>
    {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

const ChartCard = ({ title, children, right, testid }) => (
  <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const inputCls = "px-2.5 py-1.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";

const ContasDashboard = ({ contasPagar = [], faturas = [], categoriasConta = [], orcamentos = {}, salvarOrcamentos, contasReceber = [] }) => {
  const agora = new Date();
  const mesCorrente = agora.toISOString().slice(0, 7);
  const anoCorrente = String(agora.getFullYear());

  const [modo, setModo] = useState("mes");
  const [refMes, setRefMes] = useState(mesCorrente);
  const [refAno, setRefAno] = useState(anoCorrente);
  const [perInicio, setPerInicio] = useState(`${mesCorrente}-01`);
  const [perFim, setPerFim] = useState(hojeISO());
  const [compTipo, setCompTipo] = useState("meses");
  const [compRefs, setCompRefs] = useState([]);
  const [compMesInput, setCompMesInput] = useState(mesCorrente);

  const [catFiltro, setCatFiltro] = useState("todas");
  const [orcOpen, setOrcOpen] = useState(false);
  const [orcForm, setOrcForm] = useState({});
  const [exportando, setExportando] = useState(false);
  const areaRef = useRef(null);

  // Defaults do comparativo ao entrar no modo
  useEffect(() => {
    if (modo !== "comparativo") return;
    if (compTipo === "meses") {
      const [y, m] = mesCorrente.split("-").map(Number);
      const ant = new Date(y, m - 2, 1);
      setCompRefs([`${ant.getFullYear()}-${String(ant.getMonth() + 1).padStart(2, "0")}`, mesCorrente]);
    } else {
      setCompRefs([String(Number(anoCorrente) - 1), anoCorrente]);
    }
    // eslint-disable-next-line
  }, [modo, compTipo]);

  const pagas = useMemo(() => contasPagar.filter((c) => c.status === "pago" && c.dataPagamento), [contasPagar]);
  const receitasPagas = useMemo(() => [
    ...faturas.filter((f) => f.paga && f.dataPagamento && !f.cancelada).map((f) => ({ dataPagamento: f.dataPagamento, total: f.total })),
    ...(contasReceber || []).filter((c) => c.status === "recebido" && c.dataRecebimento).map((c) => ({ dataPagamento: c.dataRecebimento, total: c.valor })),
  ], [faturas, contasReceber]);

  // ---- Buckets (colunas do eixo X) conforme o modo ----
  const buckets = useMemo(() => {
    if (modo === "mes") {
      const [y, m] = refMes.split("-").map(Number);
      return Array.from({ length: diasNoMes(y, m) }, (_, i) => {
        const dd = `${refMes}-${String(i + 1).padStart(2, "0")}`;
        return { label: String(i + 1), start: dd, end: dd };
      });
    }
    if (modo === "ano") {
      return Array.from({ length: 12 }, (_, i) => {
        const mm = String(i + 1).padStart(2, "0");
        return { label: NOMES[i], start: `${refAno}-${mm}-01`, end: `${refAno}-${mm}-${String(diasNoMes(Number(refAno), i + 1)).padStart(2, "0")}` };
      });
    }
    if (modo === "periodo") {
      if (!perInicio || !perFim || perFim < perInicio) return [];
      const dias = Math.round((new Date(perFim) - new Date(perInicio)) / 86400000) + 1;
      const arr = [];
      if (dias <= 62) {
        const d = new Date(perInicio + "T00:00:00"), fim = new Date(perFim + "T00:00:00");
        while (d <= fim) { const s = iso(d); arr.push({ label: `${s.slice(8, 10)}/${s.slice(5, 7)}`, start: s, end: s }); d.setDate(d.getDate() + 1); }
      } else {
        const d = new Date(perInicio.slice(0, 7) + "-01T00:00:00"), fim = new Date(perFim.slice(0, 7) + "-01T00:00:00");
        while (d <= fim) {
          const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          const dimEnd = `${ym}-${String(diasNoMes(d.getFullYear(), d.getMonth() + 1)).padStart(2, "0")}`;
          arr.push({ label: monthLabel(ym), start: ym + "-01" < perInicio ? perInicio : ym + "-01", end: dimEnd > perFim ? perFim : dimEnd });
          d.setMonth(d.getMonth() + 1);
        }
      }
      return arr;
    }
    if (modo === "comparativo") {
      return compRefs.map((ref) => {
        if (compTipo === "anos") return { label: ref, start: `${ref}-01-01`, end: `${ref}-12-31` };
        const [y, m] = ref.split("-").map(Number);
        return { label: monthLabel(ref), start: `${ref}-01`, end: `${ref}-${String(diasNoMes(y, m)).padStart(2, "0")}` };
      });
    }
    return [];
  }, [modo, refMes, refAno, perInicio, perFim, compTipo, compRefs]);

  const agg = (start, end) => {
    const soma = (arr, dk, vk) => arr.filter((x) => { const d = x[dk]; return d && d >= start && d <= end; }).reduce((a, x) => a + (x[vk] || 0), 0);
    return { saida: soma(pagas, "dataPagamento", "valor"), entrada: soma(receitasPagas, "dataPagamento", "total") };
  };
  const inSel = (d) => !!d && buckets.some((b) => d >= b.start && d <= b.end);

  const fluxo = useMemo(() => buckets.map((b) => {
    const { entrada, saida } = agg(b.start, b.end);
    return { mes: b.label, Entradas: +entrada.toFixed(2), Saidas: +saida.toFixed(2), Saldo: +(entrada - saida).toFixed(2) };
  }), [buckets, pagas, receitasPagas]);

  const evolucao = useMemo(() => buckets.map((b) => {
    const val = pagas.filter((c) => c.dataPagamento >= b.start && c.dataPagamento <= b.end && (catFiltro === "todas" || c.categoria === catFiltro))
      .reduce((s, c) => s + (c.valor || 0), 0);
    return { mes: b.label, Custo: +val.toFixed(2) };
  }), [buckets, pagas, catFiltro]);

  const porCategoria = useMemo(() => {
    const map = {};
    // Todas as despesas do período (pagas ou não): pagas pela data de pagamento, pendentes pelo vencimento.
    contasPagar.forEach((c) => {
      const d = c.status === "pago" ? (c.dataPagamento || c.vencimento) : c.vencimento;
      if (!inSel(d)) return;
      const k = c.categoria || "Outros"; map[k] = (map[k] || 0) + (c.valor || 0);
    });
    return Object.entries(map).map(([nome, valor]) => ({ nome, valor: +valor.toFixed(2) })).sort((a, b) => b.valor - a.valor);
  }, [contasPagar, buckets]);

  const totalEntradas = fluxo.reduce((s, r) => s + r.Entradas, 0);
  const totalSaidas = fluxo.reduce((s, r) => s + r.Saidas, 0);
  const saldo = totalEntradas - totalSaidas;
  const hoje = hojeISO();
  const emAberto = contasPagar.filter((c) => c.status === "pendente").reduce((s, c) => s + (c.valor || 0), 0);
  const vencido = contasPagar.filter((c) => c.status === "pendente" && c.vencimento && c.vencimento < hoje).reduce((s, c) => s + (c.valor || 0), 0);
  const catTopo = porCategoria[0];
  const top5 = porCategoria.slice(0, 5);

  const filtroLabel = useMemo(() => {
    if (modo === "mes") return `Mês: ${monthLabel(refMes)}`;
    if (modo === "ano") return `Ano: ${refAno}`;
    if (modo === "periodo") return `Período: ${perInicio ? formatDate(perInicio) : "?"} a ${perFim ? formatDate(perFim) : "?"}`;
    return `Comparativo: ${buckets.map((b) => b.label).join("  ×  ")}`;
  }, [modo, refMes, refAno, perInicio, perFim, buckets]);

  // ---- Orçamento (gasto do mês atual por vencimento) ----
  const gastoMesPorCat = useMemo(() => {
    const map = {};
    contasPagar.forEach((c) => { if ((c.vencimento || "").slice(0, 7) !== mesCorrente) return; const k = c.categoria || "Outros"; map[k] = (map[k] || 0) + (c.valor || 0); });
    return map;
  }, [contasPagar, mesCorrente]);
  const statusOrcamentos = useMemo(() => Object.entries(orcamentos || {}).map(([cat, lim]) => {
    const gasto = gastoMesPorCat[cat] || 0; const limite = Number(lim) || 0; const pct = limite > 0 ? (gasto / limite) * 100 : 0;
    return { cat, limite, gasto, pct, estourou: gasto > limite, perto: gasto <= limite && pct >= 80 };
  }).sort((a, b) => b.pct - a.pct), [orcamentos, gastoMesPorCat]);
  const alertas = statusOrcamentos.filter((a) => a.estourou || a.perto);

  const abrirOrcamentos = () => { const init = {}; Object.entries(orcamentos || {}).forEach(([k, v]) => { init[k] = String(v); }); setOrcForm(init); setOrcOpen(true); };
  const salvarOrc = async () => { const limpo = {}; Object.entries(orcForm).forEach(([k, v]) => { const n = parseFloat(v); if (n > 0) limpo[k] = n; }); await salvarOrcamentos(limpo); setOrcOpen(false); };

  const exportarPDF = async () => {
    if (!areaRef.current) return;
    setExportando(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");
      const canvas = await html2canvas(areaRef.current, { scale: 2, backgroundColor: "#f8fafc", useCORS: true, logging: false });
      const pdf = new jsPDF("p", "mm", "a4");
      const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight(), margem = 8;
      const imgW = pw - margem * 2;
      pdf.setFontSize(14); pdf.setTextColor(30); pdf.text("Painel Analítico — Contas a Pagar", margem, 12);
      pdf.setFontSize(9); pdf.setTextColor(120);
      pdf.text(filtroLabel, margem, 17);
      pdf.text(`Gerado em ${new Date().toLocaleDateString("pt-BR")}`, pw - margem, 12, { align: "right" });
      const topo = 21;
      const alturaPaginaPx = ((ph - topo - margem) * canvas.width) / imgW;
      let sy = 0, primeira = true;
      while (sy < canvas.height) {
        const sliceH = Math.min(alturaPaginaPx, canvas.height - sy);
        const tmp = document.createElement("canvas");
        tmp.width = canvas.width; tmp.height = sliceH;
        tmp.getContext("2d").drawImage(canvas, 0, sy, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        const slH = (sliceH * imgW) / canvas.width;
        if (!primeira) pdf.addPage();
        pdf.addImage(tmp.toDataURL("image/png"), "PNG", margem, primeira ? topo : margem, imgW, slH);
        sy += sliceH; primeira = false;
      }
      pdf.save(`painel-contas-${hoje}.pdf`);
    } catch (e) { console.error(e); } finally { setExportando(false); }
  };

  const catsParaOrcar = useMemo(() => {
    const nomes = new Set((categoriasConta || []).map((c) => c.nome));
    Object.keys(orcamentos || {}).forEach((k) => nomes.add(k));
    return Array.from(nomes).sort();
  }, [categoriasConta, orcamentos]);

  const anosDisponiveis = Array.from({ length: 5 }, (_, i) => String(Number(anoCorrente) - 4 + i));
  const addCompMes = () => { if (compMesInput && !compRefs.includes(compMesInput)) setCompRefs((r) => [...r, compMesInput].sort()); };
  const toggleCompAno = (y) => setCompRefs((r) => r.includes(y) ? r.filter((x) => x !== y) : [...r, y].sort());
  const removerRef = (ref) => setCompRefs((r) => r.filter((x) => x !== ref));

  const evolTitulo = modo === "mes" ? "Evolução diária do custo" : modo === "comparativo" ? "Custo por referência" : "Evolução do custo";

  return (
    <div className="space-y-4" data-testid="contas-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-bold text-slate-800">Painel Analítico</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="gap-2" onClick={abrirOrcamentos} data-testid="orcamentos-btn"><Target className="w-4 h-4" /> Orçamentos</Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={exportarPDF} disabled={exportando} data-testid="exportar-painel-btn"><FileDown className="w-4 h-4" /> {exportando ? "Gerando..." : "Exportar PDF"}</Button>
          <div className="w-40"><Select value={catFiltro} onValueChange={setCatFiltro}>
            <SelectTrigger data-testid="dash-filtro-categoria"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-60"><SelectItem value="todas">Todas categorias</SelectItem>{categoriasConta.map((c) => <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>)}</SelectContent>
          </Select></div>
        </div>
      </div>

      {/* Barra de referência */}
      <div className="bg-white rounded-xl border border-slate-100 p-3 shadow-sm flex flex-wrap items-center gap-3" data-testid="ref-bar">
        <div className="w-44"><Select value={modo} onValueChange={setModo}>
          <SelectTrigger data-testid="dash-modo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="mes">Por mês</SelectItem>
            <SelectItem value="ano">Por ano</SelectItem>
            <SelectItem value="comparativo">Comparativo</SelectItem>
            <SelectItem value="periodo">Período (calendário)</SelectItem>
          </SelectContent>
        </Select></div>

        {modo === "mes" && (
          <div className="flex items-center gap-2"><label className="text-sm text-slate-500">Mês:</label>
            <input type="month" className={inputCls} value={refMes} onChange={(e) => setRefMes(e.target.value)} data-testid="ref-mes" /></div>
        )}
        {modo === "ano" && (
          <div className="w-32"><Select value={refAno} onValueChange={setRefAno}>
            <SelectTrigger data-testid="ref-ano"><SelectValue /></SelectTrigger>
            <SelectContent>{anosDisponiveis.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select></div>
        )}
        {modo === "periodo" && (
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm text-slate-500">De:</label>
            <input type="date" className={inputCls} value={perInicio} onChange={(e) => setPerInicio(e.target.value)} data-testid="ref-inicio" />
            <label className="text-sm text-slate-500">até:</label>
            <input type="date" className={inputCls} value={perFim} onChange={(e) => setPerFim(e.target.value)} data-testid="ref-fim" />
          </div>
        )}
        {modo === "comparativo" && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-36"><Select value={compTipo} onValueChange={(v) => { setCompTipo(v); setCompRefs([]); }}>
              <SelectTrigger data-testid="comp-tipo"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="meses">Comparar meses</SelectItem><SelectItem value="anos">Comparar anos</SelectItem></SelectContent>
            </Select></div>
            {compTipo === "meses" ? (
              <>
                <input type="month" className={inputCls} value={compMesInput} onChange={(e) => setCompMesInput(e.target.value)} data-testid="comp-mes-input" />
                <Button variant="outline" size="sm" className="gap-1" onClick={addCompMes} data-testid="comp-add-mes"><Plus className="w-4 h-4" /> Adicionar</Button>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {compRefs.map((r) => <span key={r} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-md" data-testid={`comp-chip-${r}`}>{monthLabel(r)}<button onClick={() => removerRef(r)}><X className="w-3 h-3" /></button></span>)}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-1.5 flex-wrap">
                {anosDisponiveis.map((y) => (
                  <button key={y} onClick={() => toggleCompAno(y)} data-testid={`comp-ano-${y}`}
                    className={`text-xs px-2.5 py-1.5 rounded-md border ${compRefs.includes(y) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{y}</button>
                ))}
              </div>
            )}
          </div>
        )}
        <span className="text-xs text-slate-400 ml-auto" data-testid="ref-label">{filtroLabel}</span>
      </div>

      {/* Alerta de orçamento */}
      {alertas.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="orcamento-alerta">
          <div className="flex items-center gap-2 mb-2 text-amber-800 font-semibold text-sm"><AlertTriangle className="w-4 h-4" /> Alerta de orçamento — {monthLabel(mesCorrente)}</div>
          <div className="space-y-2">
            {alertas.map((a) => (
              <div key={a.cat} data-testid={`orcamento-alerta-${a.cat}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium text-slate-700">{a.cat} {a.estourou ? <span className="text-red-600 font-semibold">· estourou o limite</span> : <span className="text-amber-600 font-semibold">· perto do limite</span>}</span>
                  <span className="text-slate-600">{formatBRL(a.gasto)} / {formatBRL(a.limite)} ({a.pct.toFixed(0)}%)</span>
                </div>
                <div className="h-2 rounded-full bg-white overflow-hidden border border-amber-100">
                  <div className={`h-full rounded-full ${a.estourou ? "bg-red-500" : "bg-amber-500"}`} style={{ width: `${Math.min(a.pct, 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div ref={areaRef} className="space-y-4 bg-slate-50 p-1 rounded-lg">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Kpi icon={TrendingUp} label="Entradas (ref.)" value={formatBRL(totalEntradas)} tint="bg-emerald-50 text-emerald-600" testid="kpi-entradas" hint="Receitas recebidas" />
          <Kpi icon={TrendingDown} label="Saídas (ref.)" value={formatBRL(totalSaidas)} tint="bg-red-50 text-red-600" testid="kpi-saidas" hint="Contas pagas" />
          <Kpi icon={Scale} label="Saldo (ref.)" value={formatBRL(saldo)} tint={saldo >= 0 ? "bg-blue-50 text-blue-600" : "bg-red-50 text-red-600"} testid="kpi-saldo" hint="Entradas − Saídas" />
          <Kpi icon={Clock} label="Em aberto (hoje)" value={formatBRL(emAberto)} tint="bg-amber-50 text-amber-600" testid="kpi-aberto" hint="Pendente de pagamento" />
          <Kpi icon={AlertTriangle} label="Vencido (hoje)" value={formatBRL(vencido)} tint="bg-red-50 text-red-600" testid="kpi-vencido" hint="Pendente e vencido" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <ChartCard title="Fluxo de Caixa (por data de pagamento)" testid="chart-fluxo"
              right={<span className="text-xs text-slate-400">{filtroLabel}</span>}>
              {buckets.length === 0 ? <p className="text-sm text-slate-400 text-center py-24">Selecione uma referência válida.</p> : (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={fluxo} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatBRL(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Entradas" fill="#059669" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Saidas" name="Saídas" fill="#dc2626" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="Saldo" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          <ChartCard title="Despesas por Categoria (pagas e a pagar)" testid="chart-categoria">
            {porCategoria.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-16">Sem despesas pagas na referência.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={porCategoria} dataKey="valor" nameKey="nome" cx="50%" cy="45%" outerRadius={85} label={(e) => e.nome}>
                    {porCategoria.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatBRL(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <ChartCard title={`${evolTitulo}${catFiltro !== "todas" ? ` — ${catFiltro}` : ""}`} testid="chart-evolucao"
              right={<span className="text-xs text-slate-400">Total saídas: <b className="text-slate-600">{formatBRL(totalSaidas)}</b></span>}>
              {buckets.length === 0 ? <p className="text-sm text-slate-400 text-center py-20">Selecione uma referência válida.</p> : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={evolucao} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatBRL(v)} />
                    <Bar dataKey="Custo" fill="#d97706" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          <ChartCard title="Onde você mais gasta" testid="chart-top-categorias"
            right={catTopo ? <span className="text-xs text-red-600 font-medium">Maior: {catTopo.nome}</span> : null}>
            {top5.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-16">Sem dados na referência.</p>
            ) : (
              <div className="space-y-3 py-2">
                {top5.map((c, i) => {
                  const pct = totalSaidas > 0 ? (c.valor / totalSaidas) * 100 : 0;
                  return (
                    <div key={c.nome} data-testid={`top-cat-${i}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-slate-700">{c.nome}</span>
                        <span className="text-slate-500">{formatBRL(c.valor)} · {pct.toFixed(0)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: PALETTE[i % PALETTE.length] }} />
                      </div>
                    </div>
                  );
                })}
                <p className="text-[11px] text-slate-400 pt-2">💡 Foque nas 2 primeiras categorias para reduzir custos com maior impacto.</p>
              </div>
            )}
          </ChartCard>
        </div>
      </div>

      {/* Modal Orçamentos */}
      <Dialog open={orcOpen} onOpenChange={setOrcOpen}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="orcamentos-modal">
          <DialogHeader><DialogTitle>Orçamento mensal por categoria</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Defina o limite de gasto por mês. O sistema avisa quando o gasto do mês (por vencimento) chegar a 80% ou ultrapassar o limite. Deixe em branco para não monitorar.</p>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {catsParaOrcar.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Cadastre categorias em "Categorias" primeiro.</p>}
            {catsParaOrcar.map((cat) => (
              <div key={cat} className="flex items-center justify-between gap-3" data-testid={`orcamento-linha-${cat}`}>
                <span className="text-sm text-slate-700 flex-1 truncate">{cat}</span>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-400">R$</span>
                  <input type="number" step="0.01" min="0" placeholder="0,00" className="w-28 px-2.5 py-1.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 text-sm text-right"
                    value={orcForm[cat] ?? ""} onChange={(e) => setOrcForm((f) => ({ ...f, [cat]: e.target.value }))} data-testid={`orcamento-input-${cat}`} />
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrcOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={salvarOrc} data-testid="salvar-orcamentos">Salvar orçamentos</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContasDashboard;
